// Workstream G2 — unverified-claim inline marker (hast-tree half).
//
// Mirrors rehypeAttributionChips, but wraps a RANGE. insertClaimSentinels
// (unverifiedMarks.ts) injects an OPEN (`\uE002 N \uE003`) / CLOSE
// (`\uE004 N \uE005`) sentinel pair into the answer STRING before parsing, so
// remark places them in the correct text nodes. This walk threads a shared
// open/close state across the tree in document order and wraps every text run
// inside an open span — including text inside inline elements (bold/links) — in
// a subtle `<u-mark>` (rendered as a muted dotted underline by MessageRenderer).

import type { UnverifiedSpan } from "./unverifiedMarks";
import { U_CLOSE, U_OPEN } from "./unverifiedMarks";

interface HastText {
  type: "text";
  value: string;
}

interface HastElement {
  type: "element";
  tagName: string;
  properties?: Record<string, unknown>;
  children: HastNode[];
}

type HastNode = HastText | HastElement;

interface Root {
  type: "root";
  children: HastNode[];
}

const SENTINEL_RE = /[\uE002\uE004](\d+)[\uE003\uE005]/g;

interface WalkState {
  open: UnverifiedSpan | null;
}

function makeMark(children: HastNode[], span: UnverifiedSpan): HastElement {
  return {
    type: "element",
    tagName: "u-mark",
    properties: { dataUnverifiedClaim: "", dataClaim: span.claim, dataFacet: span.facetId },
    children,
  };
}

// Split a sentinel-bearing text value, toggling the shared open state and
// wrapping each emitted run per the CURRENT state. An unknown/stale sentinel
// ord is silently consumed (never leaks raw PUA into the DOM).
function splitTextNode(value: string, spans: UnverifiedSpan[], state: WalkState): HastNode[] {
  const out: HastNode[] = [];
  const emit = (text: string) => {
    if (text.length === 0) return;
    out.push(state.open ? makeMark([{ type: "text", value: text }], state.open) : { type: "text", value: text });
  };
  const re = new RegExp(SENTINEL_RE);
  let last = 0;
  let m = re.exec(value);
  while (m !== null) {
    emit(value.slice(last, m.index));
    const span = spans[Number(m[1])];
    if (value[m.index] === U_OPEN) {
      if (span) state.open = span;
    } else {
      state.open = null;
    }
    last = m.index + m[0].length;
    m = re.exec(value);
  }
  emit(value.slice(last));
  return out.length === 0 ? [{ type: "text", value }] : out;
}

function walk(node: Root | HastElement, spans: UnverifiedSpan[], state: WalkState): void {
  const newChildren: HastNode[] = [];
  for (const child of node.children) {
    if (child.type === "text") {
      if (child.value.includes(U_OPEN) || child.value.includes(U_CLOSE)) {
        for (const produced of splitTextNode(child.value, spans, state)) newChildren.push(produced);
      } else if (state.open) {
        newChildren.push(makeMark([child], state.open));
      } else {
        newChildren.push(child);
      }
    } else if (child.type === "element") {
      walk(child, spans, state);
      newChildren.push(child);
    } else {
      newChildren.push(child);
    }
  }
  node.children = newChildren;
}

export function rehypeUnverifiedMarks(opts: { spans: UnverifiedSpan[] }): (tree: Root) => void {
  const { spans } = opts;
  return (tree: Root): void => {
    if (spans.length === 0) return;
    walk(tree, spans, { open: null });
  };
}
